import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, firstValueFrom, share } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AuthStore } from './auth.store';
import {
  ACEITAR_TOS_MUTATION,
  LOGIN_MUTATION,
  ME_QUERY,
  REGISTRAR_MUTATION,
} from './auth.queries';
import type {
  AuthPayload,
  LoginInput,
  RegistrarInput,
  User,
} from './auth.types';

interface RegistrarData {
  registrar: AuthPayload;
}
interface LoginData {
  login: AuthPayload;
}
interface MeData {
  me: User;
}
interface AceitarTosData {
  aceitarTos: User;
}
interface RefreshResponse {
  sessionToken: string;
}

/**
 * AuthService — orquestra GraphQL (registrar/login/me/aceitarTos) e REST
 * (refresh/logout). Persiste resultado no `AuthStore` (signals).
 *
 * Refresh inflight: quando o interceptor dispara `refresh()` em paralelo
 * em N requests 401, usamos `share()` pra single-flight — só UMA chamada
 * `/auth/refresh` é feita; todas as outras observam o resultado.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apollo = inject(Apollo);
  private readonly http = inject(HttpClient);
  private readonly store = inject(AuthStore);

  private refreshInflight$: Observable<RefreshResponse> | null = null;

  // ===========================================================================
  // GraphQL
  // ===========================================================================

  async registrar(input: RegistrarInput): Promise<User> {
    const result = await firstValueFrom(
      this.apollo.mutate<RegistrarData>({
        mutation: REGISTRAR_MUTATION,
        variables: { input },
      }),
    );
    const payload = result.data?.registrar;
    if (!payload) {
      throw new Error('Resposta vazia do backend ao registrar.');
    }
    this.store.setSession(payload.user, payload.sessionToken);
    return payload.user;
  }

  async login(input: LoginInput): Promise<User> {
    const result = await firstValueFrom(
      this.apollo.mutate<LoginData>({
        mutation: LOGIN_MUTATION,
        variables: { input },
      }),
    );
    const payload = result.data?.login;
    if (!payload) {
      throw new Error('Resposta vazia do backend ao logar.');
    }
    this.store.setSession(payload.user, payload.sessionToken);
    return payload.user;
  }

  async me(): Promise<User | null> {
    const result = await firstValueFrom(
      this.apollo.query<MeData>({
        query: ME_QUERY,
        fetchPolicy: 'network-only',
      }),
    );
    const user = result.data?.me ?? null;
    if (user) this.store.setCurrentUser(user);
    return user;
  }

  async aceitarTos(versao: string = environment.tosVersion): Promise<User> {
    const result = await firstValueFrom(
      this.apollo.mutate<AceitarTosData>({
        mutation: ACEITAR_TOS_MUTATION,
        variables: { versao },
      }),
    );
    const user = result.data?.aceitarTos;
    if (!user) {
      throw new Error('Resposta vazia ao aceitar ToS.');
    }
    this.store.setCurrentUser(user);
    return user;
  }

  // ===========================================================================
  // REST: refresh + logout
  // ===========================================================================

  /**
   * Tenta renovar sessionToken via cookie HttpOnly. Single-flight:
   * múltiplas chamadas paralelas compartilham a mesma request HTTP.
   *
   * Se sucesso: atualiza store, retorna observable do novo token.
   * Se falha (cookie ausente, expirado, revogado): limpa store, propaga erro.
   */
  refresh(): Observable<RefreshResponse> {
    if (this.refreshInflight$) {
      return this.refreshInflight$;
    }

    const req$ = this.http
      .post<RefreshResponse>(
        `${environment.apiUrl}/auth/refresh`,
        {},
        { withCredentials: true },
      )
      .pipe(
        tap((res) => this.store.setSessionToken(res.sessionToken)),
        catchError((err: unknown) => {
          this.store.clear();
          throw err;
        }),
        share(), // single-flight para chamadas paralelas
      );

    this.refreshInflight$ = req$;

    // Limpar referência quando completar (sucesso ou erro)
    req$.subscribe({
      complete: () => {
        this.refreshInflight$ = null;
      },
      error: () => {
        this.refreshInflight$ = null;
      },
    });

    return req$;
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.apiUrl}/auth/logout`,
          {},
          { withCredentials: true },
        ),
      );
    } catch {
      // Logout é idempotente — mesmo se backend falhar, limpa local.
    } finally {
      this.store.clear();
      await this.apollo.client.clearStore();
    }
  }

  // ===========================================================================
  // Hidratação no boot — APP_INITIALIZER
  // ===========================================================================

  /**
   * Chamado no boot da app pelo APP_INITIALIZER. Tenta refresh silencioso:
   * se o cookie estiver lá, recupera sessionToken + popula `me` no store.
   * Em qualquer falha, fica deslogado (rotas protegidas redirecionam pra
   * /auth/login).
   */
  async hidratarDoCookie(): Promise<void> {
    try {
      await firstValueFrom(this.refresh().pipe(map(() => undefined)));
      await this.me();
    } catch {
      // Silencioso — usuário simplesmente não estava logado.
      this.store.clear();
    }
  }

  /**
   * Lista providers OAuth ativos no backend (env-driven). Frontend usa
   * pra esconder botões sem credencial configurada. Falha silenciosa
   * → assume nenhum disponível.
   */
  async providersDisponiveis(): Promise<{
    google: boolean;
    facebook: boolean;
  }> {
    try {
      return await firstValueFrom(
        this.http.get<{ google: boolean; facebook: boolean }>(
          `${environment.apiUrl}/auth/oauth/providers`,
          { withCredentials: false },
        ),
      );
    } catch {
      return { google: false, facebook: false };
    }
  }

  /** URL de início do fluxo OAuth pro provider — fullscreen redirect. */
  oauthUrl(provider: 'google' | 'facebook'): string {
    return `${environment.apiUrl}/auth/oauth/${provider}`;
  }
}
